import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutUs = () => {
  return (
    <>
      <Head>
        <title>About — KTR Capital</title>
        <meta name="description" content="KTR Capital is a private investment firm combining a physician-led biotech edge with proprietary trading technology. Austin, Texas." />
        <meta name="robots" content="noindex" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/ktr_isotipo.svg" />
      </Head>

      <Navbar />

      {/* Hero */}
      <section className="bg-black text-white min-h-[70vh] flex items-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-orange-500/10 to-orange-600/5 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 py-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-gray-900/80 border border-gray-700/50 rounded-full text-gray-300 text-sm font-light">
              <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
              About KTR Capital
            </div>
            <h1 className="text-4xl lg:text-6xl font-light leading-[1.1] tracking-tight max-w-3xl">
              Capital with a <span className="text-orange-400">purpose</span>.
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed font-light max-w-2xl">
              KTR Capital is a private investment firm built to direct capital toward the science and
              technology shaping the future — with a particular focus on biotech companies working to make
              a real difference in cancer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link href="/sign-up" className="inline-flex items-center justify-center gap-3 bg-orange-500 text-black px-8 py-4 rounded font-medium hover:bg-orange-600 transition-colors">
                Request access <span aria-hidden="true">→</span>
              </Link>
              <Link href="/contact" className="inline-flex items-center justify-center border border-gray-700 text-gray-200 px-8 py-4 rounded font-medium hover:bg-gray-900 transition-colors">
                Contact us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Story / Mission */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Our Mission</div>
          <h2 className="text-3xl md:text-4xl font-light mb-8 leading-tight max-w-2xl">
            Built to back the science that matters.
          </h2>
          <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed max-w-3xl">
            <p>
              KTR Capital was created to operate at the intersection of rigorous market intelligence,
              disciplined capital allocation, and deep scientific conviction. We combine a physician-led
              biotech edge with proprietary trading technology — directing capital toward selected biotech
              companies working to make a real, measurable difference in cancer.
            </p>
            <p>
              That focus is not incidental. The firm's approach is informed by genuine clinical
              understanding of oncology — of drug pipelines, trial outcomes, and where the science is
              actually moving. It is medical insight applied as an investment discipline, paired with
              systematic, in-house quantitative strategies and careful risk management.
            </p>
            <p>
              We believe differentiated firms are built on proprietary systems, tighter feedback loops, and
              a willingness to stay selective. Our model is designed for qualified investors who value
              transparency, direct access, and long-duration alignment.
            </p>
          </div>
        </div>
      </section>

      {/* Austin — bioscience hub */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Based in Austin, Texas</div>
          <h2 className="text-3xl md:text-4xl font-light mb-8 leading-tight max-w-2xl">
            At the center of an emerging bioscience hub.
          </h2>
          <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed max-w-3xl">
            <p>
              KTR Capital is headquartered in Austin, Texas — one of the fastest-growing centers for life
              sciences, technology, and capital in the United States. Austin's expanding biosciences
              ecosystem, research institutions, and concentration of technical talent place the firm close
              to the innovation it seeks to back.
            </p>
            <p>
              It is a fitting home for a firm built around science and technology: a city where
              breakthrough research, entrepreneurial energy, and serious capital increasingly converge.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Firm Overview</div>
            <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight">
              Selective. Disciplined. Long-term.
            </h2>
            <p className="text-lg text-gray-400 font-light leading-relaxed">
              We are building a firm around discipline, structural insight, capacity control, and careful
              capital allocation.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-gray-900">
            {[
              ['Proprietary Intelligence', 'Internal research and signal infrastructure, built and operated in-house, designed to support differentiated decision-making across markets.'],
              ['Selective Relationships', 'We work with qualified investors and strategic partners where alignment and sophistication are clear, and we manage capacity deliberately.'],
              ['Conviction-Driven Capital', 'We focus on high-conviction opportunities — in biotech, quantitative trading, and private equity — within a single, disciplined fund.'],
            ].map(([h, t]) => (
              <div key={h} className="bg-black p-8">
                <h3 className="text-xl font-light text-white mb-3">{h}</h3>
                <p className="text-sm text-gray-400 leading-relaxed font-light">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">Next Step</div>
            <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight">
              Start the conversation.
            </h2>
            <p className="text-lg text-gray-400 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
              KTR Capital Fund I is available to accredited investors. Create an account to complete a brief
              qualification and explore the platform, or contact us directly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/sign-up" className="inline-flex items-center justify-center gap-3 bg-orange-500 text-black px-8 py-4 rounded font-medium hover:bg-orange-600 transition-colors">
                Request access <span aria-hidden="true">→</span>
              </Link>
              <Link href="/contact" className="inline-flex items-center justify-center border border-gray-700 text-gray-200 px-8 py-4 rounded font-medium hover:bg-gray-900 transition-colors">
                Contact us
              </Link>
            </div>
            <p className="text-xs text-gray-600 font-light mt-10 max-w-2xl mx-auto leading-relaxed">
              KTR Capital Management is a Texas-based Exempt Reporting Adviser; KTR Capital Fund I, L.P. is a
              Texas Limited Partnership. This page is for informational purposes only and is not an offer to
              sell or a solicitation to buy any security. Any offer is made only to accredited investors
              through definitive offering documents. Investments involve substantial risk, including the
              possible loss of all capital.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutUs;
